import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Truck,
  BarChart3,
  Users,
  UserCog,
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  Package,
  Eye
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = ({ children }) => {
  const { user, logout, embedded, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/notifications`);
        setUnreadCount(response.data.unread_count || 0);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };
    fetchNotifications();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/admin/operations?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const openCompanyPicker = async () => {
    setShowCompanyPicker(true);
    setCompaniesLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/customers`);
      setCompanies(resp.data.customers || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleImpersonate = async (customerId) => {
    try {
      const resp = await axios.post(`${API_URL}/api/admin/impersonate/${customerId}`);
      setShowCompanyPicker(false);
      // Set the customer token + user in context (triggers re-render immediately)
      startImpersonation(resp.data.access_token, resp.data.user);
      // Navigate to customer dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Impersonation failed:', err);
    }
  };

  const navItems = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/operations', icon: FileText, label: 'Operations' },
    { to: '/admin/accepted-shipments', icon: Truck, label: 'Waybills' },
    { to: '/admin/customers', icon: Users, label: 'Customers' },
  ];

  if (user?.role === 'super_admin') {
    navItems.push({ to: '/admin/reports', icon: BarChart3, label: 'Reports' });
    navItems.push({ to: '/admin/staff', icon: UserCog, label: 'Staff' });
  }

  navItems.push({ to: '/admin/settings', icon: Settings, label: 'Settings' });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Top Navigation — hidden in embedded/iframe mode */}
      {!embedded && (
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50" data-testid="admin-navbar">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <NavLink to="/admin/dashboard" className="flex items-center" data-testid="admin-logo-link">
                <img src="/cargodash-logo.svg" alt="CargoDash" className="h-9" />
              </NavLink>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={({ isActive }) =>
                    `flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'text-teal-700 bg-teal-50'
                        : 'text-slate-600 hover:text-teal-700 hover:bg-slate-50'
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 mr-1.5" />
                  {item.label}
                </NavLink>
              ))}
            </div>

            {/* Search and User */}
            <div className="flex items-center space-x-2">
              {/* Search Icon + Overlay */}
              <div className="relative">
                {searchOpen ? (
                  <form onSubmit={handleSearch} className="flex items-center">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search waybills..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
                        className="pl-10 w-56 h-9 bg-slate-50"
                        data-testid="admin-search-input"
                        autoFocus
                      />
                    </div>
                  </form>
                ) : (
                  <button
                    onClick={() => setSearchOpen(true)}
                    className="p-2 text-slate-500 hover:text-teal-700 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid="admin-search-btn"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* View as Customer (super_admin only) */}
              {user?.role === 'super_admin' && (
                <button
                  onClick={openCompanyPicker}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  data-testid="view-as-customer-btn"
                  title="View as Customer"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden xl:inline">View as Customer</span>
                </button>
              )}

              {/* Notifications */}
              <NavLink
                to="/admin/notifications"
                className="relative p-2 text-slate-500 hover:text-teal-700 rounded-lg hover:bg-slate-50"
                data-testid="admin-notifications-btn"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </NavLink>

              {/* User Avatar + Name */}
              <div className="hidden sm:flex items-center space-x-2 pl-2 border-l border-slate-100">
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900 leading-tight">{user?.full_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role === 'super_admin' ? 'Admin' : ''}</p>
                </div>
                <div className="h-9 w-9 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-teal-700 font-semibold text-sm">
                    {user?.full_name?.charAt(0) || 'A'}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                data-testid="admin-mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="admin-mobile-menu">
            <div className="px-4 py-3 space-y-1">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search waybills..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full h-10 bg-slate-50"
                  />
                </div>
              </form>

              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                      isActive
                        ? 'text-teal-700 bg-teal-50'
                        : 'text-slate-600 hover:text-teal-700 hover:bg-slate-50'
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-red-600 font-medium rounded-lg hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>
      )}

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Company Picker Modal */}
      {showCompanyPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCompanyPicker(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="company-picker-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">View as Customer</h3>
                <p className="text-sm text-slate-500 mt-0.5">Select a company to view their portal</p>
              </div>
              <button onClick={() => setShowCompanyPicker(false)} className="p-1 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-3">
              {companiesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-700"></div>
                </div>
              ) : companies.length > 0 ? (
                <div className="space-y-1">
                  {companies.map(c => (
                    <button
                      key={c.customer_id}
                      onClick={() => handleImpersonate(c.customer_id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                      data-testid={`impersonate-${c.customer_id}`}
                    >
                      <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <span className="text-indigo-600 font-semibold text-sm">{c.company_name?.charAt(0)}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{c.company_name}</p>
                        <p className="text-xs text-slate-500">{c.account_code} {c.prefix ? `· ${c.prefix}` : ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No companies found. Create one first.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
